import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from 'react-query';
import { Typography, CircularProgress, Box } from '@material-ui/core';
import { BeneficiosMGMParametros } from './BeneficiosMGMParametros/BeneficiosMGMParametros';
import { INITIAL_FILTER_STATE, INITIAL_SORT_STATE } from './BeneficiosMGMTableConstants';
import { useAuth } from '../../../../auth/authProvider';
import request from '../../../../services/network/request';
import { BeneficiosMGMParametrosTable } from './BeneficiosMGMParametros/BeneficiosMGMParametrosTable';

const BeneficiosMGM = () => {
  const { token } = useAuth();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filters, setFilters] = useState(INITIAL_FILTER_STATE);
  const [sorts, setSorts] = useState(INITIAL_SORT_STATE);
  const [selectedFilter, setSelectedFilter] = useState(undefined);

  const parseValues = useCallback(obj => {
    return Object.entries(obj)
      .filter(([, value]) => value)
      .map(([key, value]) => `${key}:${value}`)
      .join(',');
  }, []);

  const queryParams = useMemo(
    () => ({
      sort: parseValues(sorts),
      page: currentPage,
      take: rowsPerPage,
      filters: parseValues(filters),
    }),
    [parseValues, sorts, currentPage, rowsPerPage, filters],
  );

  const { data, isFetching: isLoading } = useQuery(
    ['fetch-clientes-bs', currentPage, rowsPerPage, filters, sorts],
    () => request.get('backstage/comunidade/mgm-benefits', { token, params: queryParams }),
    {
      keepPreviousData: true,
      staleTime: 30000,
    },
  );

  const { data: grantedCount } = useQuery(
    ['fetch-count-mgm', queryParams],
    () => {
      return request.get('backstage/comunidade/mgm-benefits/granted-count', {
        token,
        params: { filters: queryParams.filters },
      });
    },
    {
      refetchOnWindowFocus: false,
      staleTime: 0,
    },
  );

  const tableData = data?.data || [];
  const total = data?.pagination?.totalRecords || 0;

  const handleChangePage = useCallback((_, newPage) => {
    setCurrentPage(newPage);
  }, []);

  const handleChangeRowsPerPage = useCallback(event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setCurrentPage(1);
  }, []);

  const changeFilters = useCallback(filter => {
    setCurrentPage(1);
    setFilters(filter);
  }, []);

  return (
    <>
      <BeneficiosMGMParametros
        filters={filters}
        setFilters={changeFilters}
        hasFastFilterSelected={!!selectedFilter}
        setSelectedFilter={setSelectedFilter}
        grantedCount={grantedCount}
      />

      {isLoading && (
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      )}

      {!isLoading && tableData.length > 0 && (
        <BeneficiosMGMParametrosTable
          data={tableData}
          setSorts={setSorts}
          sorts={sorts}
          total={total}
          rowsPerPage={rowsPerPage}
          currentPage={currentPage}
          handleChangePage={handleChangePage}
          handleChangeRowsPerPage={handleChangeRowsPerPage}
        />
      )}

      {!isLoading && tableData.length === 0 && (
        <Typography align="center" variant="body1" color="textSecondary" style={{ marginTop: 24 }}>
          Ops. Não encontramos nada.
        </Typography>
      )}
    </>
  );
};

export { BeneficiosMGM };
